/* eslint react/require-default-props: ["error", { "functions": "defaultArguments" }] */
import React from 'react';
import styled from '@emotion/styled';
import { heavy, semiBold, colors } from '@/styles';
import { WarningCircle, Info, SuccessCircle } from '@/svgs';
import { type NoticeColor, type NoticeType } from '@/constants';
import { StyledComponent } from '@/helpers';
import { isColorName } from '@/styles/colorUtils';

type BaseNoticeBannerProps = StyledComponent & {
  noticeText: string;
  type?: NoticeType;
  actionText?: string;
  onActionClick?: React.MouseEventHandler | null;
};

type OriginalBrandNoticeBannerProps = BaseNoticeBannerProps & {
  /** @deprecated Only available for old brand. Use noticeColor with a proper color instead */
  color?: NoticeColor;
  isRebrand?: false;
};

type NoticeBannerPropsWithNoticeColor = BaseNoticeBannerProps & {
  noticeColor?: string;
  isRebrand?: boolean;
};

export type NoticeBannerProps = OriginalBrandNoticeBannerProps | NoticeBannerPropsWithNoticeColor;

/**
 * NoticeBanner component
 */
function NoticeBanner(props: NoticeBannerProps) {
  const {
    className,
    noticeText,
    onActionClick = null,
    actionText = 'Click here',
    type = 'none',
  } = props;

  let icon = null;
  switch (type) {
    case 'info':
      icon = <Info fill={colors.white} size={`${16.6 / 2.85}`} />;
      break;
    case 'warning':
      icon = <WarningCircle fill={colors.white} size={`${16.6 / 2.85}`} />;
      break;
    case 'success':
      icon = <SuccessCircle fill={colors.white} size={`${16.6 / 2.85}`} />;
      break;
  }

  return (
    <div className={className}>
      {icon}
      <span className="notice">{noticeText}</span>
      {onActionClick && (
        <button type="button" onClick={onActionClick}>
          {actionText}
        </button>
      )}
    </div>
  );
}

const styledNoticeBanner = styled(NoticeBanner)`
  ${(props) => (props.isRebrand ? semiBold() : heavy())}
  font-size: 0.875rem;
  display: flex;
  color: ${colors.white};
  justify-content: center;
  align-items: center;
  padding: 0.5rem;
  transition: 0.3s;
  background-color: ${(props) => {
    if ('noticeColor' in props && props.noticeColor) {
      return props.noticeColor;
    }
    if ('color' in props && props.color && isColorName(props.color)) {
      return colors[props.color];
    }
    return 'inherit';
  }};

  svg {
    margin-right: 0.461875rem;
  }

  button {
    ${(props) => (props.isRebrand ? semiBold() : heavy())}
    padding: 0 0 0 0.3rem;
    margin: 0;
    font-size: 0.875rem;
    background: none;
    border: none;
    text-decoration: underline;
    color: ${colors.white};
    cursor: pointer;
  }
`;

export default styledNoticeBanner;
